import { useEffect } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { isAxiosError } from 'axios';
import { clearCustomerInfo, setCustomerInfo } from '../store/slices/customerSlice';
import { loginSuccess, logout, setAuthInitialized, setSessionReady } from '../store/slices/authSlice';
import { listenToIdTokenChanges } from '../firebase/auth';
import { resetAllState } from '../store/slices/rootReducer';
import { persistor } from '../store';
import { forgetPersonInThisBrowser } from '../services/forgetThisBrowser';
import { getCustomerInfo, syncBackendSession } from '../services/firebaseAuthService';
import { getAccessLevel, isKnownRole } from '../constants/rbac';
import type { Role } from '../constants/rbac';

/**
 * The RBAC claims a verified ID token carries, validated. Exported as a pure
 * function so the gating is unit-testable without mocking Firebase.
 *
 * FAIL CLOSED: anything that is not one of the seven exact role strings
 * (missing claim, unknown string, wrong case, an array - "one role per user
 * per tenant" forbids arrays) resolves to role null, which grants nothing
 * anywhere. tenantId must be a non-empty string or it is null.
 */
export const rbacClaimsFromToken = (
  claims: unknown
): { role: Role | null; tenantId: string | null } => {
  const record = (claims && typeof claims === 'object' ? claims : {}) as Record<string, unknown>;
  const role = isKnownRole(record.role) ? record.role : null;
  const tenantId =
    typeof record.tenantId === 'string' && record.tenantId.length > 0 ? record.tenantId : null;

  return { role, tenantId };
};

const FirebaseAuthSessionBridge = () => {
  const dispatch = useDispatch();
  const store = useStore();

  useEffect(() => {
    const unsubscribe = listenToIdTokenChanges(async (user) => {
      try {
        if (!user) {
          // SIGNED OUT IN ANOTHER TAB (28 Oct, item 3 review). Firebase tells every open tab; the tab that signed out
          // clears itself (services/signOut.ts), but this one still holds the person's whole store in memory and would
          // write it back to the browser on its next change. So when someone WAS signed in here: every slice reset,
          // the saved copy removed, the browser keys forgotten. A visitor who was never signed in keeps today's path.
          const auth = (store.getState() as { auth?: { firebaseUid?: string | null; isAuthenticated?: boolean } }).auth;
          if (auth?.firebaseUid || auth?.isAuthenticated) {
            dispatch(resetAllState());
            forgetPersonInThisBrowser();
            persistor.purge().catch(() => console.error('Sign-out: the saved store could not be removed'));
          }
          dispatch(logout());
          dispatch(clearCustomerInfo());
          return;
        }

        const token = await user.getIdToken();
        const tokenResult = await user.getIdTokenResult();
        // The custom claims ride the same token result the expiry already
        // comes from. This listener fires on every token change - login,
        // refresh, claims update after re-login - so the store's role always
        // mirrors the CURRENT verified token, never a remembered one.
        const { role, tenantId } = rbacClaimsFromToken(tokenResult.claims);

        dispatch(
          loginSuccess({
            expiresAt: tokenResult.expirationTime,
            email: user.email || null,
            emailVerified: user.emailVerified === true,
            uid: user.uid,
            role,
            tenantId,
          })
        );

        try {
          await syncBackendSession(token);
          // The __session cookie now exists. Only NOW may the homepage's
          // cookie-only fetches fire - this is the signal they gate on, set
          // strictly after the sync resolves and never in the `finally` (a
          // failed sync leaves it false, so a doomed cookieless fetch is not
          // issued). Distinct from setAuthInitialized below on purpose.
          dispatch(setSessionReady(true));
        } catch {
          // A fixed line: the error carries the request, whose body and headers hold the token.
          console.error('Failed to sync backend auth session');
        }

        // NO ACCESS, NO CUSTOMER DATA (27 Oct, step 7). An account the waiting page is for (no role, or a role that
        // reads its orders only for its own company and has no company) has no customer record to read, and the waiting
        // page makes no data call; the session sync above is the sign-in itself, not data.
        const waiting = role === null || (getAccessLevel(role, 'licence.read') === 'own' && !tenantId);
        if (!waiting) {
          try {
            const customerInfo = await getCustomerInfo();
            dispatch(setCustomerInfo(customerInfo));
          } catch (error) {
            if (!isAxiosError(error)) {
              throw error;
            }
          }
        }
      } finally {
        dispatch(setAuthInitialized(true));
      }
    });

    return () => unsubscribe();
  }, [dispatch, store]);

  return null;
};

export default FirebaseAuthSessionBridge;
